import { useState } from 'react';
import { Button, Input, Textarea } from '@/shared/ui/atoms';
import { getApiErrorMessage } from '@/shared/lib/getApiErrorMessage';
import { dayBefore } from './historyEntries';
import * as s from './WorklogHistoryReader.css';

interface Props {
  readonly todayDate: string;
  /** 이미 일지가 있는 날짜 — 고르면 저장을 막고 목록에서 고치도록 안내한다. */
  readonly loggedDates: ReadonlySet<string>;
  readonly onCreateLog: (logDate: string, content: string) => Promise<unknown>;
}

/**
 * 일지가 없던 날 쓰기 — 저장만 한다. 지난 날 일지는 마감하지 않는다(서버도 400): 마감이 오늘
 * 태스크를 그 날로 옮기기 때문이다. 팀장 화면에서는 그 날이 지금처럼 「미제출」로 남는다.
 */
export function MissingDayForm({ todayDate, loggedDates, onCreateLog }: Props) {
  const [open, setOpen] = useState(false);
  const [logDate, setLogDate] = useState('');
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const duplicate = !!logDate && loggedDates.has(logDate);
  const latest = dayBefore(todayDate);
  const canSave = !!logDate && logDate <= latest && !duplicate && !!memo.trim() && !saving;

  function reset() {
    setOpen(false);
    setLogDate('');
    setMemo('');
    setError(null);
  }

  function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    onCreateLog(logDate, memo)
      .then(reset)
      .catch((e: unknown) => setError(getApiErrorMessage(e, '일지를 저장하지 못했습니다.')))
      .finally(() => setSaving(false));
  }

  if (!open) {
    return (
      <Button size="xs" variant="link" onClick={() => setOpen(true)}>
        빠진 날 일지 쓰기
      </Button>
    );
  }

  return (
    <div className={s.context}>
      <Input
        type="date"
        label="날짜"
        fullWidth
        max={latest}
        value={logDate}
        error={duplicate ? '이 날은 이미 일지가 있습니다. 목록에서 메모를 고쳐 주세요.' : undefined}
        onChange={(event) => setLogDate(event.currentTarget.value)}
      />
      <Textarea
        label="빠진 날 메모"
        fullWidth
        rows={4}
        value={memo}
        error={error ?? undefined}
        onChange={(event) => setMemo(event.currentTarget.value)}
      />
      <p className={s.small}>저장한 일지는 마감하지 않은 일지로 남습니다.</p>
      <div className={s.actions}>
        <Button size="sm" onClick={save} disabled={!canSave}>
          일지 저장
        </Button>
        <Button size="sm" variant="ghost" onClick={reset} disabled={saving}>
          취소
        </Button>
      </div>
    </div>
  );
}
